import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  IconArrowLeft,
  IconUserPlus,
  IconEdit,
  IconTrash,
  IconPlayerPlay,
  IconPlayerStop,
  IconVolume,
  IconSettings,
  IconPhone,
  IconMusic,
  IconMessage,
  IconSearch
} from '@tabler/icons-react';
import { useQueue } from '../contexts/QueueContext';
import { useSipNumber } from '../contexts/SipNumberContext';

interface IvrMenu {
  _id?: string;
  name: string;
  description: string;
  isActive: boolean;
  welcomeMessage: string;
  options: IvrOption[];
  defaultAction: 'queue' | 'message' | 'hangup';
  defaultTarget?: string;
  timeout: number; // seconds
  maxRetries: number;
}

interface IvrOption {
  key: string; // 1, 2, 3, etc.
  label: string;
  action: 'queue' | 'message' | 'hangup' | 'submenu';
  target?: string; // queue ID, message text, or submenu ID
  description: string;
}

const IvrManagement: React.FC = () => {
  const [ivrMenus, setIvrMenus] = useState<IvrMenu[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState<IvrMenu | null>(null);
  const [formData, setFormData] = useState<IvrMenu>({
    name: '',
    description: '',
    isActive: true,
    welcomeMessage: '',
    options: [],
    defaultAction: 'queue',
    defaultTarget: '',
    timeout: 10,
    maxRetries: 3
  });

  const queueContext = useQueue();
  const sipNumberContext = useSipNumber();

  // Fetch IVR menus
  const fetchIvrMenus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/ivr-menus', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIvrMenus(data.data || []);
      } else {
        setError('IVR menüleri yüklenemedi');
      }
    } catch (err) {
      setError('IVR menüleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIvrMenus();
    // SIP numaralarını yükle
    if (sipNumberContext.sipNumbers.length === 0) {
      sipNumberContext.fetchSipNumbers();
    }
  }, []);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = editingMenu ? `/api/ivr-menus/${editingMenu._id}` : '/api/ivr-menus';
      const method = editingMenu ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess(editingMenu ? 'IVR menüsü güncellendi' : 'IVR menüsü oluşturuldu');
        setDialogOpen(false);
        setEditingMenu(null);
        resetForm();
        fetchIvrMenus();
      } else {
        setError('IVR menüsü kaydedilemedi');
      }
    } catch (err) {
      setError('IVR menüsü kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      isActive: true,
      welcomeMessage: '',
      options: [],
      defaultAction: 'queue',
      defaultTarget: '',
      timeout: 10,
      maxRetries: 3
    });
  };

  // Open dialog for new menu
  const handleNewMenu = () => {
    resetForm();
    setEditingMenu(null);
    setDialogOpen(true);
  };

  // Open dialog for editing
  const handleEditMenu = (menu: IvrMenu) => {
    setFormData(menu);
    setEditingMenu(menu);
    setDialogOpen(true);
  };

  // Delete menu
  const handleDeleteMenu = async (id: string) => {
    if (!window.confirm('Bu IVR menüsünü silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/ivr-menus/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccess('IVR menüsü silindi');
        fetchIvrMenus();
      } else {
        setError('IVR menüsü silinemedi');
      }
    } catch (err) {
      setError('IVR menüsü silinemedi');
    } finally {
      setLoading(false);
    }
  };

  // Toggle menu active status
  const handleToggleActive = async (menu: IvrMenu) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/ivr-menus/${menu._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...menu, isActive: !menu.isActive })
      });

      if (response.ok) {
        setSuccess(`IVR menüsü ${!menu.isActive ? 'aktif' : 'pasif'} edildi`);
        fetchIvrMenus();
      } else {
        setError('IVR menüsü güncellenemedi');
      }
    } catch (err) {
      setError('IVR menüsü güncellenemedi');
    }
  };

  // Add new option
  const addOption = () => {
    const newOption: IvrOption = {
      key: (formData.options.length + 1).toString(),
      label: '',
      action: 'queue',
      target: '',
      description: ''
    };
    setFormData({
      ...formData,
      options: [...formData.options, newOption]
    });
  };

  // Update option
  const updateOption = (index: number, field: keyof IvrOption, value: string) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setFormData({ ...formData, options: newOptions });
  };

  // Remove option
  const removeOption = (index: number) => {
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({ ...formData, options: newOptions });
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundImage: 'url(./bg.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      p: 3
    }}>
      {/* Geri İkonu */}
      <Box sx={{ mb: 3 }}>
        <Box 
          sx={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            p: 1,
            borderRadius: 2,
            backgroundColor: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
              transform: 'translateX(-2px)'
            }
          }}
          onClick={() => window.history.back()}
        >
          <IconArrowLeft size={24} color="white" />
        </Box>
      </Box>

      {/* Ana İçerik */}
      <Box sx={{ px: 10 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="600" color="white" sx={{ fontFamily: 'Monaco, monospace', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
            IVR Menü Yönetimi
          </Typography>
          <Button
            variant="contained"
            startIcon={<IconUserPlus size={20} />}
            onClick={handleNewMenu}
            sx={{
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' },
              fontFamily: 'Monaco, monospace',
              fontWeight: 600
            }}
          >
            Yeni IVR Menüsü
          </Button>
        </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading && <CircularProgress sx={{ mb: 2 }} />}

        <Grid container spacing={3}>
          {ivrMenus.map((menu) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={menu._id}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                border: '1px solid #E0E0E0',
                background: 'white'
              }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" fontWeight="600" sx={{ fontFamily: 'Monaco, monospace' }}>
                      {menu.name}
                    </Typography>
                    <Chip
                      label={menu.isActive ? 'Aktif' : 'Pasif'}
                      color={menu.isActive ? 'success' : 'default'}
                      size="small"
                      sx={{ fontFamily: 'Monaco, monospace' }}
                    />
                  </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {menu.description}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Hoş Geldin Mesajı:
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    "{menu.welcomeMessage}"
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Seçenekler ({menu.options.length}):
                  </Typography>
                  {menu.options.slice(0, 3).map((option) => (
                    <Chip
                      key={option.key}
                      label={`${option.key}: ${option.label}`}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                  {menu.options.length > 3 && (
                    <Typography variant="caption" color="text.secondary">
                      +{menu.options.length - 3} daha...
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                  <Button
                    size="small"
                    startIcon={<IconEdit size={20} />}
                    onClick={() => handleEditMenu(menu)}
                  >
                    Düzenle
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<IconTrash size={20} />}
                    onClick={() => handleDeleteMenu(menu._id!)}
                  >
                    Sil
                  </Button>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={menu.isActive}
                        onChange={() => handleToggleActive(menu)}
                        size="small"
                      />
                    }
                    label=""
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* IVR Menu Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMenu ? 'IVR Menüsünü Düzenle' : 'Yeni IVR Menüsü'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Menü Adı"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Açıklama"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Hoş Geldin Mesajı"
                  value={formData.welcomeMessage}
                  onChange={(e) => setFormData({ ...formData, welcomeMessage: e.target.value })}
                  multiline
                  rows={2}
                  placeholder="Örn: Merhaba, size nasıl yardımcı olabilirim?"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Zaman Aşımı (saniye)"
                  type="number"
                  value={formData.timeout}
                  onChange={(e) => setFormData({ ...formData, timeout: parseInt(e.target.value) })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Maksimum Deneme"
                  type="number"
                  value={formData.maxRetries}
                  onChange={(e) => setFormData({ ...formData, maxRetries: parseInt(e.target.value) })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Varsayılan Aksiyon</InputLabel>
                  <Select
                    value={formData.defaultAction}
                    onChange={(e) => setFormData({ ...formData, defaultAction: e.target.value as any })}
                  >
                    <MenuItem value="queue">Kuyruğa Yönlendir</MenuItem>
                    <MenuItem value="message">Mesaj Oku</MenuItem>
                    <MenuItem value="hangup">Kapat</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {formData.defaultAction === 'queue' && (
                <Grid size={12}>
                  <FormControl fullWidth>
                    <InputLabel>Varsayılan Kuyruk</InputLabel>
                    <Select
                      value={formData.defaultTarget || ''}
                      onChange={(e) => setFormData({ ...formData, defaultTarget: e.target.value })}
                    >
                      {queueContext.queues.map((queue) => (
                        <MenuItem key={queue._id} value={queue._id}>
                          {queue.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Menü Seçenekleri</Typography>
              <Button startIcon={<IconUserPlus size={20} />} onClick={addOption}>
                Seçenek Ekle
              </Button>
            </Box>

            {formData.options.map((option, index) => (
              <Paper key={index} sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid size={2}>
                    <TextField
                      label="Tuş"
                      value={option.key}
                      onChange={(e) => updateOption(index, 'key', e.target.value)}
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid size={3}>
                    <TextField
                      label="Etiket"
                      value={option.label}
                      onChange={(e) => updateOption(index, 'label', e.target.value)}
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Aksiyon</InputLabel>
                      <Select
                        value={option.action}
                        onChange={(e) => updateOption(index, 'action', e.target.value)}
                      >
                        <MenuItem value="queue">Kuyruğa Yönlendir</MenuItem>
                        <MenuItem value="message">Mesaj Oku</MenuItem>
                        <MenuItem value="hangup">Kapat</MenuItem>
                        <MenuItem value="submenu">Alt Menü</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={3}>
                    {option.action === 'queue' ? (
                      <FormControl size="small" fullWidth>
                        <InputLabel>Kuyruk</InputLabel>
                        <Select
                          value={option.target || ''}
                          onChange={(e) => updateOption(index, 'target', e.target.value)}
                        >
                          {queueContext.queues.map((queue) => (
                            <MenuItem key={queue._id} value={queue._id}>
                              {queue.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        label="Hedef"
                        value={option.target || ''}
                        onChange={(e) => updateOption(index, 'target', e.target.value)}
                        size="small"
                        fullWidth
                      />
                    )}
                  </Grid>
                  <Grid size={1}>
                    <IconButton
                      color="error"
                      onClick={() => removeOption(index)}
                      size="small"
                    >
                      <IconTrash size={20} />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>İptal</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Kaydet'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      </Box>
    </Box>
  );
};

export default IvrManagement;
